export interface HelpFaq {
  q: string;
  a: string;
  to?: string;
  toLabel?: string;
}

export interface HelpTopic {
  id: string;
  title: string;
  icon: string;
  blurb: string;
  faqs: HelpFaq[];
}

/**
 * In-portal quick answers for the admin team. Each answer can deep-link to the
 * exact module that performs the action, so staff never have to leave the
 * portal to figure out how something works.
 */
export const adminHelpTopics: HelpTopic[] = [
  {
    id: "bookings",
    title: "Bookings & Guests",
    icon: "ri-calendar-check-line",
    blurb: "Confirm, manage and look after every guest.",
    faqs: [
      {
        q: "How do I confirm a booking?",
        a: "Open the booking from the Bookings module and set its status to Confirmed. Confirming also triggers the automatic review-request email once email is connected. If you only want to nudge a guest for a review, use 'Send review nudge' in the guest follow-up card — it re-sends the review email without touching the booking status.",
        to: "/admin/bookings",
        toLabel: "Open Bookings",
      },
      {
        q: "How do I check a guest in on arrival?",
        a: "Use the Check-ins module on the day of arrival. Pick the date, find the guest and mark them checked in, or flag a no-show.",
        to: "/admin/check-ins",
        toLabel: "Open Check-ins",
      },
      {
        q: "How do I re-send a review request to a guest?",
        a: "Open the booking and use the 'Send review nudge' button in the guest follow-up card. It re-sends the email without changing the booking status.",
        to: "/admin/bookings",
        toLabel: "Open Bookings",
      },
      {
        q: "Can I export a list of bookings for farm staff?",
        a: "Yes. Every Bookings and Check-ins view has CSV and PDF export buttons that export exactly what you have filtered on screen.",
        to: "/admin/bookings",
        toLabel: "Open Bookings",
      },
    ],
  },
  {
    id: "reviews",
    title: "Reviews & Reputation",
    icon: "ri-star-line",
    blurb: "Approve, curate and promote guest reviews.",
    faqs: [
      {
        q: "How do I approve a pending review?",
        a: "Open the Reviews module. Anything waiting sits in the pending queue at the top, and you can approve them one by one or use 'Approve all'.",
        to: "/admin/reviews",
        toLabel: "Open Reviews",
      },
      {
        q: "How do I show a great review on the homepage?",
        a: "In the review drawer, use 'Approve & feature on homepage'. It publishes the review and adds it to the homepage testimonials in one click. Run it again on a review you have already featured and it updates that same testimonial instead of creating a duplicate.",
        to: "/admin/reviews",
        toLabel: "Open Reviews",
      },
      {
        q: "Where do guest visit photos appear?",
        a: "Submitted photos show in the review drawer and also in the guest's booking detail panel, so you can preview the trip before following up.",
        to: "/admin/reviews",
        toLabel: "Open Reviews",
      },
    ],
  },
  {
    id: "calendar",
    title: "Calendar & Availability",
    icon: "ri-calendar-2-line",
    blurb: "Control when visits can and cannot happen.",
    faqs: [
      {
        q: "How do I block dates for maintenance or holidays?",
        a: "Open the Calendar, select the day and use the 'Block dates' action. The block is saved straight away and shows on the month view.",
        to: "/admin/calendar",
        toLabel: "Open Calendar",
      },
      {
        q: "How do I set recurring weekly availability?",
        a: "Use the Availability module to add weekly slots per farm or experience, with capacity and guest limits. Blocked dates stay in sync with the calendar.",
        to: "/admin/availability",
        toLabel: "Open Availability",
      },
    ],
  },
  {
    id: "content",
    title: "Content & SEO",
    icon: "ri-pages-line",
    blurb: "Publish and optimise everything on the public site.",
    faqs: [
      {
        q: "How do I edit the homepage hero or navigation?",
        a: "Open 'Homepage' to edit the brand identity, hero copy, navigation links and footer. Saving updates the live site immediately.",
        to: "/admin/homepage",
        toLabel: "Open Homepage",
      },
      {
        q: "How do I change a page's search title and description?",
        a: "Open the SEO module and pick the page to edit its title, meta description and keywords. Live length hints keep them within Google's limits, and changes save automatically and go live on the site right away.",
        to: "/admin/seo",
        toLabel: "Open SEO",
      },
      {
        q: "How do I edit the search text for the detail pages?",
        a: "The Experiences, Farms, Farmers and Stories entries in the SEO module control every detail page under that section. Keep the {title} and {description} placeholders in the text so each item still shows its own wording.",
        to: "/admin/seo",
        toLabel: "Open SEO",
      },
      {
        q: "How do I publish a blog post or farmer story?",
        a: "Use the Blog or Farmer Stories module. You can publish immediately, save as a draft, or schedule it to go live automatically on a chosen date.",
        to: "/admin/blog",
        toLabel: "Open Blog",
      },
    ],
  },
  {
    id: "farm-network",
    title: "Farm Network",
    icon: "ri-plant-line",
    blurb: "Onboard and look after your farm partners.",
    faqs: [
      {
        q: "How do I approve a farmer application?",
        a: "Open Farmer Applications, review the details and move it through the pipeline to Approved. New farmers then appear in the Farmers module.",
        to: "/admin/farmer-applications",
        toLabel: "Open Applications",
      },
      {
        q: "How do I add a brand-new farmer or farm?",
        a: "Both the Farmers and Farms modules have a 'New' button in the header that opens a create form and saves a real record.",
        to: "/admin/farmers",
        toLabel: "Open Farmers",
      },
    ],
  },
  {
    id: "money",
    title: "Revenue, Refunds & Payouts",
    icon: "ri-line-chart-line",
    blurb: "Track income, issue refunds and settle with farmers.",
    faqs: [
      {
        q: "Where do I see revenue and platform commission?",
        a: "The Revenue module shows gross, commission, farmer payouts and pending amounts, with a monthly and per-farm breakdown you can export.",
        to: "/admin/revenue",
        toLabel: "Open Revenue",
      },
      {
        q: "How do I process a farmer settlement?",
        a: "Open Settlements, find the payout and move its status from Pending to Processing, then to Paid as the money leaves.",
        to: "/admin/settlements",
        toLabel: "Open Settlements",
      },
      {
        q: "How do I issue a refund?",
        a: "Open Refunds, find the original payment and issue a full or partial refund. The refund is recorded against the booking and updates the payment record, so the numbers always stay in step.",
        to: "/admin/refunds",
        toLabel: "Open Refunds",
      },
      {
        q: "How do I create a discount code?",
        a: "Open Coupons to create a code, choose an amount or percentage off, and set an expiry date or usage limit. An active code is available at checkout straight away.",
        to: "/admin/coupons",
        toLabel: "Open Coupons",
      },
    ],
  },
  {
    id: "messaging",
    title: "Email & Messaging",
    icon: "ri-mail-send-line",
    blurb: "Reach guests across email, SMS and WhatsApp.",
    faqs: [
      {
        q: "Why aren't emails actually being sent?",
        a: "Automatic emails go out through the platform email service, Resend. It needs to be connected in Integrations with a verified sending domain — once that is done, confirmation and review emails start flowing with no further setup.",
        to: "/admin/integrations",
        toLabel: "Open Integrations",
      },
      {
        q: "How do I run a campaign?",
        a: "Open Campaigns to create and manage email, SMS or WhatsApp campaigns, then track reach and open rates.",
        to: "/admin/campaigns",
        toLabel: "Open Campaigns",
      },
    ],
  },
  {
    id: "team",
    title: "Team & Access",
    icon: "ri-group-line",
    blurb: "Give the right people the right access.",
    faqs: [
      {
        q: "How do I give a new teammate access to the portal?",
        a: "Open Admin Users and add their name and email along with a starting role. They get access to the portal with only what that role is allowed to see.",
        to: "/admin/admin-users",
        toLabel: "Open Admin Users",
      },
      {
        q: "How do I set what each role can see and do?",
        a: "Use Roles & Permissions to choose which modules and actions each role can reach. Any change applies to everyone who is on that role.",
        to: "/admin/roles-permissions",
        toLabel: "Open Roles & Permissions",
      },
    ],
  },
  {
    id: "media",
    title: "Media & Assets",
    icon: "ri-image-2-line",
    blurb: "Upload once, then reuse photos everywhere.",
    faqs: [
      {
        q: "How do I upload a photo and use it as a cover or hero?",
        a: "Open the Media Library and upload the image, then pick it from the media picker wherever an image is requested — blog covers, story images, farm galleries or the homepage hero.",
        to: "/admin/media-library",
        toLabel: "Open Media Library",
      },
      {
        q: "Can I reuse the same image in several places?",
        a: "Yes. Anything in the Media Library can be selected again for any cover, gallery or banner, so uploading once and reusing keeps the whole site consistent.",
        to: "/admin/media-library",
        toLabel: "Open Media Library",
      },
    ],
  },
  {
    id: "safety",
    title: "Safety & Compliance",
    icon: "ri-shield-check-line",
    blurb: "Keep visits safe and records in order.",
    faqs: [
      {
        q: "Where do I log an incident or a near-miss?",
        a: "Open Incidents to record what happened, who was involved and the action taken. Every entry is time-stamped, so your safety record stays complete.",
        to: "/admin/incidents",
        toLabel: "Open Incidents",
      },
      {
        q: "How do I keep safety and compliance paperwork in order?",
        a: "Use Documents to store certificates and licences, and Compliance to track the items that must stay current. Expiring items are flagged so nothing quietly lapses.",
        to: "/admin/compliance",
        toLabel: "Open Compliance",
      },
    ],
  },
  {
    id: "reports",
    title: "Reports & Exports",
    icon: "ri-file-chart-line",
    blurb: "Pull the numbers and take them with you.",
    faqs: [
      {
        q: "How do I export a list to Excel or PDF?",
        a: "Most modules — Bookings, Revenue, Settlements and the Reports pages — have CSV and PDF export buttons. Each one exports exactly what you have filtered on screen.",
        to: "/admin/analytics",
        toLabel: "Open Reports",
      },
      {
        q: "Where do I find platform-wide analytics?",
        a: "The Reports section covers analytics, bookings, revenue, farm performance, customers and marketing, each with its own date range picker and exports.",
        to: "/admin/analytics",
        toLabel: "Open Analytics",
      },
    ],
  },
];

export const platformSupport = {
  docs: "https://docs.readdy.ai",
  docsLabel: "Readdy help & documentation",
  blurb:
    "For platform-level questions — publishing, domains, billing or the Readdy editor itself — the official documentation is the fastest place to start.",
};